import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { Logo } from "@/components/Logo";
import { PrismBackground } from "@/components/Prism";
import {
  AXOR_EMAIL,
  AXOR_PHONE,
  AXOR_ADDRESS_LINES,
  telLink,
  mailtoLink,
} from "@/config";

const nav = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Jobs", to: "/jobs" },
  { label: "Contact", to: "/contact" },
];

export const Footer = () => (
  <footer
    className="relative overflow-hidden border-t border-white/[0.06] bg-axor-deep"
    data-testid="footer"
  >
    <PrismBackground />
    <div className="relative mx-auto w-full max-w-7xl px-6 py-16 md:px-10 md:py-20">
      <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1.2fr]">
        <div>
          <Logo />
          <p className="mt-6 max-w-sm text-lg font-medium leading-relaxed text-axor-white/80">
            Shaping Success, One Career at a Time.
          </p>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-axor-text">
            Career and recruitment technology for students, graduates, Green
            Card holders, professionals and employers across the United States.
          </p>
        </div>

        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.22em] text-axor-cyan">
            Navigate
          </h3>
          <ul className="mt-6 space-y-3">
            {nav.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  className="text-sm text-axor-white/70 transition-colors hover:text-axor-white"
                  data-testid={`footer-${n.label.toLowerCase().replace(/\s/g, "-")}-link`}
                >
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.22em] text-axor-cyan">
            Get in touch
          </h3>
          <ul className="mt-6 space-y-4 text-sm">
            <li className="flex items-start gap-3 text-axor-white/70">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-axor-cyan" />
              <span>
                {AXOR_ADDRESS_LINES.map((line) => (
                  <span key={line} className="block">
                    {line}
                  </span>
                ))}
              </span>
            </li>
            <li>
              <a
                href={mailtoLink()}
                className="flex items-center gap-3 text-axor-white/70 transition-colors hover:text-axor-white"
                data-testid="footer-email-link"
              >
                <Mail className="h-4 w-4 shrink-0 text-axor-cyan" />
                {AXOR_EMAIL}
              </a>
            </li>
            <li>
              <a
                href={telLink}
                className="flex items-center gap-3 text-axor-white/70 transition-colors hover:text-axor-white"
                data-testid="footer-phone-link"
              >
                <Phone className="h-4 w-4 shrink-0 text-axor-cyan" />
                {AXOR_PHONE}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/[0.06] pt-8 text-xs text-axor-text sm:flex-row sm:items-center">
        <p>© {new Date().getFullYear()} Axor Technologies. All rights reserved.</p>
        <div className="flex gap-6">
          <Link to="/privacy" className="transition-colors hover:text-axor-white" data-testid="footer-privacy-link">
            Privacy Policy
          </Link>
          <Link to="/terms" className="transition-colors hover:text-axor-white" data-testid="footer-terms-link">
            Terms &amp; Conditions
          </Link>
        </div>
      </div>
    </div>
  </footer>
);
